import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, object, string } from 'yup';

export type SetupProviderFormState = {
  name: string | null;
  apiUrl: string | null;
  syncRoundEnd: string | boolean | null;
  status: string;
  retryScheduledSendCredit: string | boolean | null;
  refundOnCreditFail: string | boolean | null;
  filterExternalProviderBets: string | boolean | null;
  isInternal: string | boolean | null;
  integrationType: string | null;
  syncRaindrop: string | boolean | null;
  syncFreebet: string | boolean | null;
  notifyOnLastFreebet: string | boolean | null;
  enableInactivityScreen: string | boolean | null;
  raindropType: string | null;
};

export const setupProviderFormStateDefault: SetupProviderFormState = {
  name: '',
  apiUrl: '',
  syncRoundEnd: '',
  status: '',
  retryScheduledSendCredit: '',
  refundOnCreditFail: '',
  filterExternalProviderBets: '',
  isInternal: '',
  integrationType: '',
  syncRaindrop: '',
  syncFreebet: '',
  notifyOnLastFreebet: '',
  enableInactivityScreen: '',
  raindropType: '',
};

export const setupProviderSchemaDefault: ObjectSchema<Partial<SetupProviderFormState>> =
  object<SetupProviderFormState>().shape({
    name: string().required('name is required field'),
    apiUrl: string().required('api url is required field'),
    syncRoundEnd: string().required('sync round end is required field'),
    status: string().required('status is required field'),
    retryScheduledSendCredit: string().required('retry scheduled send credit is required field'),
    refundOnCreditFail: string().required('refund on credit fail is required field'),
    filterExternalProviderBets: string().required(
      'filter external provider bets is required field'
    ),
    isInternal: string().required('is internal is required field'),
    integrationType: string().required('integration type is required field'),
    syncRaindrop: string().required('sync raindrop is required field'),
    syncFreebet: string().required('sync freebet is required field'),
    notifyOnLastFreebet: string().required('notify on last freebet is required field'),
    enableInactivityScreen: string().required('enable inactivity screen is required field'),
    raindropType: string().required('raindrop type is required field'),
  });

type SetupProviderFormContextType = {
  schema: ObjectSchema<Partial<SetupProviderFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<SetupProviderFormState>>>;
  methods?: UseFormReturn<SetupProviderFormState>;
};

const createProviderFormContextDefault = {
  schema: setupProviderSchemaDefault,
  setSchema: noop,
} satisfies SetupProviderFormContextType;

export const SetupProviderFormContext = createContext<SetupProviderFormContextType>(
  createProviderFormContextDefault
);

export const useSetupProviderForm = () => {
  const { methods } = useContext(SetupProviderFormContext);
  if (!methods) {
    throw new Error('SetupProviderFormContext is empty');
  }

  return methods;
};

export const useManageSetupProviderSchema = () => {
  const { setSchema } = useContext(SetupProviderFormContext);

  return { setSchema };
};
